/**
 * Popularidade híbrida (0..100%):
 * - wPos: peso da pontuação por posição
 *   (1º lugar vale mais; 100% só se TODOS colocarem em 1º)
 * - wFreq: peso da frequência absoluta (vezesEscolhido / totalUsuarios)
 * - k: expoente para “empinar” o topo (1 = linear; >1 favorece quem está mais alto)
 *
 * Regra importante:
 *   100% só acontece se:
 *   - o candidato foi colocado em 1º por todos os usuários
 *   - e não recebeu votos em posições inferiores
 *
 * @param {Object} candidatosFS - dados.candidatos do Firestore
 * @param {number} totalPosicoes - nº de slots da categoria (ex.: 10, 5, 6…)
 * @param {number} [totalUsuarios] - se não vier, tenta calcular via mapa `usuarios`
 * @param {number} [wPos=0.7]
 * @param {number} [wFreq=0.3]
 * @param {number} [k=1.0]
 * @returns {Object} { [chaveCandidato]: percentNumber }
 */
export const calcularPopularidadePonderada = (
  candidatosFS,
  totalPosicoes = 10,
  totalUsuarios,
  wPos = 0.7,
  wFreq = 0.3,
  k = 1.0
) => {
  const candidatos = candidatosFS || {};

  // 1) Descobrir total de usuários de forma dinâmica
  let N =
    typeof totalUsuarios === "number" && totalUsuarios > 0
      ? totalUsuarios
      : 0;

  if (N === 0) {
    // fallback: tenta contar pelo mapa usuarios de um candidato qualquer
    const anyCandidato = Object.values(candidatos)[0];
    const usuariosMap =
      (anyCandidato && anyCandidato.usuarios) || null; // alguns modelos guardam aqui

    if (usuariosMap && typeof usuariosMap === "object") {
      N = Object.keys(usuariosMap).length;
    }
  }

  // evita divisão por 0
  if (!Number.isFinite(N) || N <= 0) N = 1;

  // 2) Pontuação por posição (maior peso para posições altas)
  // totalPosicoes pode ser 5, 6, 10...
  // Ex.: totalPosicoes = 6 -> 1º = 6, 2º = 5, ..., 6º = 1
  const pontosPorPos = (pos) =>
    Math.max(1, totalPosicoes + 1 - parseInt(pos, 10)); // 1º -> totalPosicoes

  const scorePos = {}; // soma ponderada por posições (bruta)
  const freqAbs = {}; // vezesEscolhido / N

  for (const [chave, dados] of Object.entries(candidatos)) {
    let soma = 0;
    const posicoes = dados.posicoes || {};

    for (const [pos, vezes] of Object.entries(posicoes)) {
      soma += pontosPorPos(pos) * (Number(vezes) || 0);
    }
    scorePos[chave] = soma;

    const vezesEscolhido = Number(dados.vezesEscolhido) || 0;
    // 0..1, clampado
    freqAbs[chave] = Math.min(1, Math.max(0, vezesEscolhido / N));
  }

  // 3) Máximo teórico: todos os usuários colocam o filme em 1º lugar
  //    Isso independe de quantas posições existem (5, 6, 10...)
  const pesoPrimeiro = pontosPorPos(1); // ex.: totalPosicoes
  const maxTeorico =
    pesoPrimeiro > 0 && N > 0 ? pesoPrimeiro * N : 0;

  // 4) Normalização + mistura com pesos + curva k
  const resultado = {};

  for (const [chave] of Object.entries(candidatos)) {
    // posição normalizada em relação ao máximo teórico (0..1)
    const posNorm =
      maxTeorico > 0 ? scorePos[chave] / maxTeorico : 0;

    // frequência absoluta normalizada (0..1)
    const freqNorm = freqAbs[chave];

    // mistura (0..1)
    let blend = wPos * posNorm + wFreq * freqNorm;

    // curva (empinar topo)
    if (k !== 1) {
      blend = Math.pow(blend, k);
    }

    // percent final (0..100), arredondado para 1 casa
    const pct = Math.min(
      100,
      Math.max(0, Number((blend * 100).toFixed(1)))
    );

    resultado[chave] = pct;
  }

  return resultado;
};

export default calcularPopularidadePonderada;
